/**
 * PWA（Progressive Web App）基盤のセットアップ。
 *
 * 本番（セルフホスト）では Service Worker（/sw.js）と
 * manifest（/manifest.json）が Nginx によりルート直下に配信される前提で、
 * ここで manifest リンクの動的注入と SW 登録を行う。
 *
 * 注意: Readdy プレビュー（__IS_PREVIEW__ = true）では SW / manifest が
 * 配信されないため、登録処理をスキップして通常の SPA として動作する。
 * 登録失敗（404 など）も致命的にはせず、コンソールに警告を出すだけにする。
 */

/** ルート直下に配信される静的ファイルのパス */
const SW_PATH = '/sw.js';
const MANIFEST_PATH = '/manifest.json';
const ICON_192_PATH = '/icons/icon-192.png';

/** アプリのテーマカラー（後続でオフィステーマに追従させることも可能） */
const THEME_COLOR = '#d92d20';

/** ベースパスを考慮した公開パスを組み立てる（本番はルート配信のため /xxx になる） */
function resolvePublicPath(path: string): string {
  const base = __BASE_PATH__ || '/';
  const trimmedBase = base === '/' ? '' : base.replace(/\/$/, '');
  return `${trimmedBase}${path}`;
}

/** <head> に meta タグを動的注入する（既存があれば上書きしない） */
function ensureMeta(name: string, content: string): void {
  if (document.querySelector(`meta[name="${name}"]`)) return;
  const meta = document.createElement('meta');
  meta.name = name;
  meta.content = content;
  document.head.appendChild(meta);
}

/** manifest へのリンクを動的注入する（index.html を直接いじれないため） */
function injectManifestLink(): void {
  if (document.querySelector('link[rel="manifest"]')) return;
  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = resolvePublicPath(MANIFEST_PATH);
  document.head.appendChild(link);
}

/** iOS Safari のホーム画面追加用アイコンと表示設定を注入する */
function injectAppleMeta(): void {
  if (!document.querySelector('link[rel="apple-touch-icon"]')) {
    const icon = document.createElement('link');
    icon.rel = 'apple-touch-icon';
    icon.href = resolvePublicPath(ICON_192_PATH);
    document.head.appendChild(icon);
  }
  ensureMeta('mobile-web-app-capable', 'yes');
  ensureMeta('apple-mobile-web-app-status-bar-style', 'default');
}

/** Service Worker を登録する（ページ描画を阻害しないよう load 後に遅延実行） */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] このブラウザは Service Worker 非対応です');
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(resolvePublicPath(SW_PATH))
      .then((registration) => {
        console.info('[PWA] Service Worker 登録完了:', registration.scope);
        // 後続フェーズ（Web Push）ではここで Push 購読・通知許可処理を追加する
      })
      .catch((error) => {
        console.warn('[PWA] Service Worker 登録に失敗:', error);
      });
  });
}

/** PWA 基盤のセットアップを一括実行するエントリポイント */
export function registerPwa(): void {
  // プレビュー環境では SW / manifest が配信されないためスキップ
  if (__IS_PREVIEW__) return;
  if (typeof window === 'undefined') return;

  injectManifestLink();
  ensureMeta('theme-color', THEME_COLOR);
  injectAppleMeta();
  registerServiceWorker();
}

/* ------------------------------------------------------------------ *
 * 表示モード判定（インストール済みアプリ or 通常のブラウザ）
 * ------------------------------------------------------------------ */

/** 表示モード。browser 以外は「インストール済みアプリ相当」として扱う。 */
export type DisplayMode =
  | 'browser'
  | 'standalone'
  | 'minimal-ui'
  | 'fullscreen'
  | 'window-controls-overlay';

/**
 * 現在の表示モードを判定する。
 * `display-mode` メディアクエリで standalone / minimal-ui / fullscreen /
 * window-controls-overlay のいずれかに一致すればアプリ表示とみなす。
 */
export function getDisplayMode(): DisplayMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'browser';
  const modes: Exclude<DisplayMode, 'browser'>[] = [
    'window-controls-overlay',
    'fullscreen',
    'standalone',
    'minimal-ui',
  ];
  const matched = modes.find((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
  return matched ?? 'browser';
}

/**
 * インストール済みアプリ（PWA / TWA）として表示されているかを判定する。
 * - display-mode が browser 以外（standalone / minimal-ui / fullscreen 等）
 * - iOS Safari の独自フラグ `navigator.standalone`
 * - Android TWA の referrer（`android-app://`）
 */
export function isStandaloneApp(): boolean {
  if (typeof window === 'undefined') return false;
  if (getDisplayMode() !== 'browser') return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  if (typeof document !== 'undefined' && document.referrer.startsWith('android-app://')) return true;
  return false;
}

/** iOS（iPhone / iPad / iPod。iPadOS のデスクトップ扱いも補正）かどうか */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iphone|ipad|ipod/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ は MacIntel として判定されるため、タッチ点数で補正する
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** iOS Safari（iOS 版 Chrome / Firefox / Edge などを除く）かどうか */
export function isIosSafari(): boolean {
  if (!isIosDevice() || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /safari/i.test(ua) && !/(crios|fxios|edgios|opios)/i.test(ua);
}

/**
 * インストール済みアプリの「場所」を案内するための端末種別。
 * ホーム画面・アプリ一覧・スタートメニューなど、起動導線が端末ごとに
 * 異なるため、案内文を出し分けるために使う。
 */
export type PwaPlatform = 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'other';

/** 現在の端末種別を推定する（案内文の出し分け用） */
export function getPwaPlatform(): PwaPlatform {
  if (typeof navigator === 'undefined') return 'other';
  // iOS（iPadOS のデスクトップ判定も isIosDevice が吸収する）
  if (isIosDevice()) return 'ios';
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  const platform = `${navigator.platform || ''} ${ua}`;
  if (/win/i.test(platform)) return 'windows';
  if (/mac/i.test(platform) && navigator.maxTouchPoints <= 1) return 'macos';
  if (/linux|x11|cros/i.test(platform)) return 'linux';
  return 'other';
}

/* ------------------------------------------------------------------ *
 * インストール促し（beforeinstallprompt）の捕捉
 * ------------------------------------------------------------------ */

/** Chrome 系が発火する `beforeinstallprompt` イベントの最小インターフェース */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const installPromptSubscribers = new Set<(event: BeforeInstallPromptEvent | null) => void>();

function notifyInstallPrompt(): void {
  installPromptSubscribers.forEach((listener) => listener(deferredInstallPrompt));
}

// モジュール読み込み時点で購読しておく（React のマウント前に発火しても取りこぼさない）
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // 既定のミニバーを抑止し、アプリ側の UI からインストールさせる
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    notifyInstallPrompt();
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    notifyInstallPrompt();
  });
}

/** 保留中のインストール促しイベントを返す（未取得なら null） */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredInstallPrompt;
}

/** インストール促しイベントの取得可否を購読する（解除関数を返す） */
export function subscribeInstallPrompt(
  listener: (event: BeforeInstallPromptEvent | null) => void,
): () => void {
  installPromptSubscribers.add(listener);
  return () => {
    installPromptSubscribers.delete(listener);
  };
}

/**
 * ネイティブのインストール促しダイアログを表示する。
 * イベント未取得（iOS や既にプロンプト済み）の場合は 'unavailable' を返す。
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredInstallPrompt) return 'unavailable';
  const promptEvent = deferredInstallPrompt;
  await promptEvent.prompt();
  const choice = await promptEvent.userChoice;
  // 一度使ったプロンプトは再利用できないため破棄する
  deferredInstallPrompt = null;
  notifyInstallPrompt();
  return choice.outcome;
}

/* ------------------------------------------------------------------ *
 * インストールご案内の表示/非表示（「今後表示しない」の記憶）
 *
 * モーダル本体（PwaInstallPrompt）と、通知設定パネルの
 * 「もう一度表示する」リセットボタン（NotificationBell）の双方から
 * 同じ状態を参照・更新できるよう、モジュール内で状態を共有する。
 * ------------------------------------------------------------------ */

/** 「今後表示しない」を記憶するキー（端末単位） */
const PWA_PROMPT_DISMISS_KEY = 'as-pwa-install-prompt-dismissed';

function readPromptDismissed(): boolean {
  try {
    return localStorage.getItem(PWA_PROMPT_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

let promptDismissed = typeof window !== 'undefined' ? readPromptDismissed() : false;
const promptDismissSubscribers = new Set<(dismissed: boolean) => void>();

function notifyPromptDismissed(): void {
  promptDismissSubscribers.forEach((listener) => listener(promptDismissed));
}

/** インストールご案内を「今後表示しない」で閉じたかどうか */
export function isPwaInstallPromptDismissed(): boolean {
  return promptDismissed;
}

/** インストールご案内を閉じて端末に記憶する */
export function dismissPwaInstallPrompt(): void {
  promptDismissed = true;
  try {
    localStorage.setItem(PWA_PROMPT_DISMISS_KEY, '1');
  } catch {
    /* 記憶できなくても閉じる */
  }
  notifyPromptDismissed();
}

/** 記憶を消して、インストールご案内をもう一度表示できるようにする */
export function resetPwaInstallPrompt(): void {
  promptDismissed = false;
  try {
    localStorage.removeItem(PWA_PROMPT_DISMISS_KEY);
  } catch {
    /* 失敗しても状態は更新する */
  }
  notifyPromptDismissed();
}

/** 「今後表示しない」の変化を購読する（解除関数を返す） */
export function subscribePwaInstallPrompt(
  listener: (dismissed: boolean) => void,
): () => void {
  promptDismissSubscribers.add(listener);
  return () => {
    promptDismissSubscribers.delete(listener);
  };
}

/* ------------------------------------------------------------------ *
 * インストールご案内モーダルの「表示リクエスト」
 *
 * 通知はアプリ（インストール済み）でのみ使えるようにしたため、
 * 未インストールの端末で「通知をオン」を押したときは、その場で
 * インストールご案内モーダルを開く導線が必要になる。
 * 通知まわりのフックからこのリクエストを発火し、PwaInstallPrompt 側の
 * 購読者がモーダルを開く。
 * ------------------------------------------------------------------ */

const installGuideRequestSubscribers = new Set<() => void>();

/** インストールご案内モーダルの表示をリクエストする */
export function requestPwaInstallGuide(): void {
  installGuideRequestSubscribers.forEach((listener) => listener());
}

/** 表示リクエストを購読する（解除関数を返す） */
export function subscribePwaInstallGuideRequest(listener: () => void): () => void {
  installGuideRequestSubscribers.add(listener);
  return () => {
    installGuideRequestSubscribers.delete(listener);
  };
}

/* ------------------------------------------------------------------ *
 * 「アプリの場所」案内モーダルの表示リクエスト
 *
 * インストールした（またはインストールしようとしている）端末で、
 * アプリのアイコンがどこに配置されるかを端末ごとに案内するための導線。
 * 通知設定パネルやログイン画面、インストール完了時の自動表示から
 * このリクエストを発火し、モーダル側の購読者が開く。
 * ------------------------------------------------------------------ */

const locationGuideRequestSubscribers = new Set<() => void>();

/** 「アプリの場所」案内モーダルの表示をリクエストする */
export function requestPwaLocationGuide(): void {
  locationGuideRequestSubscribers.forEach((listener) => listener());
}

/** 表示リクエストを購読する（解除関数を返す） */
export function subscribePwaLocationGuideRequest(listener: () => void): () => void {
  locationGuideRequestSubscribers.add(listener);
  return () => {
    locationGuideRequestSubscribers.delete(listener);
  };
}